const nullRouteSegment = '__none__';

type SyntheticStageId = {
  pipelineId: string | null;
  stageName: string;
};

type StageRunPathInput = SyntheticStageId & {
  stageExecutionId: string;
};

const encodeRouteSegment = (value: string | null): string =>
  encodeURIComponent(value ?? nullRouteSegment);

const decodeRouteSegment = (value: string | undefined): string | null => {
  if (value === undefined) {
    return null;
  }

  const decodedValue = decodeURIComponent(value);
  return decodedValue === nullRouteSegment ? null : decodedValue;
};

export const parseSyntheticStageId = (value: string): SyntheticStageId | null => {
  const separatorIndex = value.indexOf(':');
  if (separatorIndex <= 0 || separatorIndex >= value.length - 1) {
    return null;
  }

  const pipelineIdSegment = value.slice(0, separatorIndex);
  const stageName = value.slice(separatorIndex + 1);
  if (stageName.length === 0) {
    return null;
  }

  return {
    pipelineId: pipelineIdSegment === 'null' ? null : pipelineIdSegment,
    stageName,
  };
};

export const buildSyntheticStageId = (
  pipelineId: string | null | undefined,
  stageName: string,
): string => `${pipelineId ?? 'null'}:${stageName}`;

export const buildSyntheticStageIdFromRoute = (params: {
  stageSlug?: string;
  pipelineId?: string;
  stageName?: string;
}): string | null => {
  const stageSlug = decodeRouteSegment(params.stageSlug);
  if (stageSlug && parseSyntheticStageId(stageSlug)) {
    return stageSlug;
  }

  const pipelineId = decodeRouteSegment(params.pipelineId);
  const stageName = decodeRouteSegment(params.stageName);
  if (!stageName || stageName.trim().length === 0) {
    return null;
  }

  return buildSyntheticStageId(pipelineId, stageName);
};

const buildStructuredStagePath = (params: SyntheticStageId): string =>
  `/stages/${encodeRouteSegment(buildSyntheticStageId(params.pipelineId, params.stageName))}`;

export const buildStageDetailPath = (stageIdOrParams: string | SyntheticStageId): string => {
  if (typeof stageIdOrParams !== 'string') {
    return buildStructuredStagePath(stageIdOrParams);
  }

  const parsed = parseSyntheticStageId(stageIdOrParams);
  if (!parsed) {
    return `/stages/legacy/${encodeURIComponent(stageIdOrParams)}`;
  }

  return buildStructuredStagePath(parsed);
};

export const buildStageExecutionDetailPath = (
  stageExecutionIdOrParams: string | StageRunPathInput,
): string => {
  if (typeof stageExecutionIdOrParams === 'string') {
    return `/stages/executions/${encodeURIComponent(stageExecutionIdOrParams)}`;
  }

  return `${buildStructuredStagePath(stageExecutionIdOrParams)}/runs/${encodeURIComponent(stageExecutionIdOrParams.stageExecutionId)}`;
};
